import React, { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { useLanguage } from '../../contexts/LanguageContext'
import { dictionaries } from '../../i18n/dictionaries'

const publicUrl = process.env.PUBLIC_URL || ''
const SLIDE_INTERVAL_MS = 7000

const HERO_SLIDE_POSITIONS = ['center center', 'center center', 'right center', 'center center']

const HERO_SLIDE_IMAGES = [
  `${publicUrl}/img/hero-slide-engineering.webp`,
  `${publicUrl}/img/hero-slide-team.webp`,
  `${publicUrl}/img/hero-slide-digital.webp`,
  `${publicUrl}/img/hero-slide-growth.webp`,
]

const HERO_OVERLAY =
  'linear-gradient(180deg, rgba(10, 25, 47, 0.9) 0%, rgba(10, 25, 47, 0.78) 40%, rgba(10, 25, 47, 0.85) 100%)'

const SLIDE_TRANSITION = { duration: 0.85, ease: [0.4, 0, 0.2, 1] }
const TEXT_TRANSITION = { duration: 0.65, ease: [0.4, 0, 0.2, 1] }

function HeroSlideTitle({ slide }) {
  const fullTitle = `${slide.titleBefore}${slide.titleHighlight}${slide.titleAfter || ''}`.trim()
  const words = fullTitle.split(/\s+/).filter(Boolean)

  if (words.length <= 1) {
    return <span className="text-emerald-300">{fullTitle}</span>
  }

  const first = words[0]
  const last = words[words.length - 1]
  const middle = words.slice(1, -1).join(' ')

  return (
    <>
      <span className="text-emerald-300">{first}</span>
      {middle ? ` ${middle} ` : ' '}
      <span className="text-emerald-300">{last}</span>
    </>
  )
}

const HeroSection = () => {
  const { locale, t } = useLanguage()
  const [activeIndex, setActiveIndex] = useState(0)
  const [prevIndex, setPrevIndex] = useState(0)

  const slides = useMemo(() => {
    const fromLocale = dictionaries[locale]?.hero?.slides
    const fallback = dictionaries.pt?.hero?.slides
    return Array.isArray(fromLocale) && fromLocale.length > 0
      ? fromLocale
      : Array.isArray(fallback)
        ? fallback
        : []
  }, [locale])

  useEffect(() => {
    const preload = (src) => {
      const img = new Image()
      img.src = src
    }

    if (HERO_SLIDE_IMAGES[0]) preload(HERO_SLIDE_IMAGES[0])

    const preloadRest = () => {
      HERO_SLIDE_IMAGES.slice(1).forEach(preload)
    }

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(preloadRest, { timeout: 4000 })
      return () => window.cancelIdleCallback(idleId)
    }

    const timeoutId = window.setTimeout(preloadRest, 1500)
    return () => window.clearTimeout(timeoutId)
  }, [])

  const isTransitioning = activeIndex !== prevIndex

  useEffect(() => {
    if (slides.length <= 1) return undefined
    const id = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length)
    }, SLIDE_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [slides.length])

  const slide = slides[activeIndex] ?? slides[0]

  if (!slide) return null

  return (
    <section className="relative min-h-screen overflow-hidden bg-black">
      <div className="absolute inset-0 overflow-hidden" aria-hidden>
        {/* Camada base: imagem anterior sempre visível por baixo */}
        <div
          className="absolute inset-0 bg-cover bg-no-repeat brightness-[0.92] saturate-[0.92]"
          style={{
            backgroundImage: `url(${HERO_SLIDE_IMAGES[prevIndex]})`,
            backgroundPosition: HERO_SLIDE_POSITIONS[prevIndex] ?? 'center center',
          }}
        />

        {/* Entrada: crossfade + zoom (Ken Burns) — a base mantém a imagem anterior visível */}
        {isTransitioning ? (
          <motion.div
            key={activeIndex}
            className="absolute inset-0 bg-cover bg-no-repeat brightness-[0.92] saturate-[0.92]"
            style={{
              backgroundImage: `url(${HERO_SLIDE_IMAGES[activeIndex]})`,
              backgroundPosition: HERO_SLIDE_POSITIONS[activeIndex] ?? 'center center',
            }}
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={SLIDE_TRANSITION}
            onAnimationComplete={() => setPrevIndex(activeIndex)}
          />
        ) : null}
      </div>

      {/* Filtro azul fixo — cobre as imagens, nunca exposto sozinho nas transições */}
      <div
        className="pointer-events-none absolute inset-0 z-[3]"
        style={{ background: HERO_OVERLAY }}
        aria-hidden
      />

      <div className="relative z-10 flex min-h-screen flex-col">
        <div className="flex flex-1 flex-col justify-center px-4 pb-16 pt-[calc(4rem+2.75rem)] shadow-none sm:px-6 sm:pb-20 sm:pt-[calc(4rem+3.25rem)] lg:px-8 lg:py-28">
          <div className="container-max mt-[3vh] w-full md:mt-[10vh]">
            <div className="mx-auto max-w-3xl text-left lg:mx-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={TEXT_TRANSITION}
                >
                  <div className="mb-8 flex items-center gap-4">
                    <span className="h-px w-10 shrink-0 bg-white/50" aria-hidden />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-300">
                      {slide.eyebrow}
                    </span>
                  </div>

                  <h1 className="font-heading text-2xl font-bold leading-[1.2] text-white drop-shadow-sm sm:text-3xl lg:text-[2.35rem] lg:leading-tight">
                    <HeroSlideTitle slide={slide} />
                  </h1>

                  <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg lg:text-xl">
                    {slide.lead}
                  </p>
                </motion.div>
              </AnimatePresence>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, delay: 0.2 }}
                className="mt-10 flex flex-wrap items-center gap-8 sm:gap-10"
              >
                <a
                  href="#services"
                  className="group inline-flex items-center gap-3 border border-white bg-white px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-brand-navy transition-colors duration-200 hover:bg-slate-100 rounded-none"
                >
                  {t('hero.ctaServices')}
                  <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
                </a>

                <Link
                  to="/contato"
                  className="group inline-flex items-center gap-2 border-0 bg-transparent p-0 text-xs font-semibold uppercase tracking-[0.14em] text-white underline-offset-4 hover:text-sky-200 hover:underline"
                >
                  {t('hero.ctaContact')}
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0 opacity-90 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
              </motion.div>

              {slides.length > 1 && (
                <div
                  className="mt-10 flex items-center gap-2"
                  role="tablist"
                  aria-label={t('hero.carouselNav')}
                >
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      role="tab"
                      aria-selected={index === activeIndex}
                      aria-label={`${index + 1} / ${slides.length}`}
                      onClick={() => {
                        setActiveIndex(index)
                      }}
                      className="flex min-h-[44px] min-w-[44px] items-center justify-center"
                    >
                      <span
                        aria-hidden
                        className={[
                          'block h-1.5 rounded-full transition-all duration-300',
                          index === activeIndex
                            ? 'w-8 bg-white'
                            : 'w-1.5 bg-white/35',
                        ].join(' ')}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.42 }}
          className="mt-auto w-full border-t border-white/10 bg-[rgb(6_18_38)]/55 backdrop-blur-[2px]"
        >
          <div className="grid w-full grid-cols-3 divide-x divide-white/10 text-center sm:divide-white/15">
            <div className="px-3 py-8 sm:px-6 sm:py-10 lg:px-8">
              <div className="font-heading text-3xl font-bold tabular-nums text-white sm:text-4xl lg:text-[2.5rem]">
                500+
              </div>
              <div className="mt-2 text-[10px] font-semibold uppercase leading-snug tracking-[0.18em] text-slate-400 sm:text-[11px]">
                {t('hero.stat1')}
              </div>
            </div>
            <div className="px-3 py-8 sm:px-6 sm:py-10 lg:px-8">
              <div className="font-heading text-3xl font-bold tabular-nums text-white sm:text-4xl lg:text-[2.5rem]">
                98%
              </div>
              <div className="mt-2 text-[10px] font-semibold uppercase leading-snug tracking-[0.18em] text-slate-400 sm:text-[11px]">
                {t('hero.stat2')}
              </div>
            </div>
            <div className="px-3 py-8 sm:px-6 sm:py-10 lg:px-8">
              <div className="font-heading text-3xl font-bold tabular-nums text-white sm:text-4xl lg:text-[2.5rem]">
                24/7
              </div>
              <div className="mt-2 text-[10px] font-semibold uppercase leading-snug tracking-[0.18em] text-slate-400 sm:text-[11px]">
                {t('hero.stat3')}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default HeroSection
